/*
Hook for managing bulk vehicle publication queue
Handles sequential publishing of multiple vehicles to avoid API rate limits
Autor: Assistant
Fecha: 2025-06-24
*/
import { useState, useCallback } from 'react'

import ChaInputVehicle from '../interfaces/ChaInputVehicle'
import YapoInputVehicle from '../interfaces/YapoInputVehicle'
import { invertedCharacteristics } from '../utils/characteristicTranslate'
import { validateFields, Rule } from '../utils/validateForm'
import { customPost } from '../utils/customFetch'

import useMeliPublisher from './useMeliPublisher'
import useChaPublisher from './useChaPublisher'
import useYapoPublisher from './useYapoPublisher'
import useMeliFetcher from './useMeliFetcher'
import useMeliPublishingCore from './useMeliPublishingCore'
import { useAuth } from './useAuth'
import useLocalization from './useLocalization'
import usePublicationDataSaver from './usePublicationDataSaver'

export interface QueueItem {
  vehicleId: string
  vehicle: any // Vehicle object
  branch: any // Branch object
  formData?: any // Form data from user edits
  platform: 'mercadolibre' | 'chileautos' | 'yapo' | 'all' // Platform to publish to
  status: 'pending' | 'publishing' | 'completed' | 'failed'
  error?: string
  result?: any
  // NEW: Support for unpublish operations
  operation: 'publish' | 'unpublish'
  platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'> // NEW: Specific platforms for unpublish
}

export interface PublishQueueState {
  items: QueueItem[]
  isProcessing: boolean
  currentIndex: number
  totalItems: number
  completedCount: number
  failedCount: number
  // NEW: Auto-completion state management
  completionState: 'processing' | 'completed' | 'delaying' | 'auto-closing' | null
  autoCloseCountdown: number
  canAutoClose: boolean
}

const usePublishQueue = () => {
  const [queueState, setQueueState] = useState<PublishQueueState>({
    items: [],
    isProcessing: false,
    currentIndex: -1,
    totalItems: 0,
    completedCount: 0,
    failedCount: 0,
    // NEW: Initialize auto-completion state
    completionState: null,
    autoCloseCountdown: 0,
    canAutoClose: false,
  })

  const { meliPublish, meliSetPublished } = useMeliPublisher()
  const { chaPublish, chaSetPublished } = useChaPublisher()
  const { yapoPublish, yapoSetPublished } = useYapoPublisher()
  const { userData } = useAuth()
  const { fetchMunicipalities, fetchRegionDetail } = useLocalization()
  const { meliCategoryAttr } = useMeliFetcher()
  const { buildMeliPayload, buildStreetAddress } = useMeliPublishingCore()
  const { loadPublicationData } = usePublicationDataSaver()

  // NEW: Platform-specific unpublishing functions
  const unpublishFromMercadoLibre = useCallback(
    async (vehicle: any) => {
      try {
        const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api/'}meli-delete`
        await customPost(apiUrl, { _id: vehicle._id, profile: userData?.profile?._id })

        return { ok: true, message: 'Successfully unpublished from MercadoLibre' }
      } catch (error) {
        throw error
      }
    },
    [userData]
  )

  const unpublishFromChileAutos = useCallback(async (vehicle: any) => {
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api/'}cha_delete`
      await customPost(apiUrl, { _id: vehicle._id })

      return { ok: true, message: 'Successfully unpublished from ChileAutos' }
    } catch (error) {
      throw error
    }
  }, [])

  const unpublishFromYapo = useCallback(async (vehicle: any) => {
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api/'}yapo_delete`
      await customPost(apiUrl, { _id: vehicle._id })

      return { ok: true, message: 'Successfully unpublished from Yapo' }
    } catch (error) {
      throw error
    }
  }, [])

  // Platform-specific publishing functions
  const publishToMercadoLibre = useCallback(
    async (item: QueueItem) => {
      if (!userData?.organization) {
        throw new Error('User data or organization not available')
      }

      if (!userData.organization.external?.meli?.enabled) {
        throw new Error('MercadoLibre publishing not enabled for this organization')
      }

      // CRITICAL FIX 1: Load draft data first (same as form components)
      const draftResult = await loadPublicationData(item.vehicle._id, 'meli')
      const draftData = draftResult.success && draftResult.data ? draftResult.data : {}

      // Combine user form data with draft data (form data takes priority)
      const userFormData = { ...draftData, ...(item.formData || {}) }

      // Use exact same description handling as individual publishing (Meli.tsx)
      const validateDescription = (description: string): boolean => {
        return description.trim().length >= 55
      }

      // Apply exact same validation as individual publishing
      const fieldsToValidate = [
        { name: 'comments', value: userFormData.comments || item.vehicle.comments || '', rule: Rule.REQUIRED },
      ]

      const commentValidationErrors = validateFields(fieldsToValidate)

      if (Object.keys(commentValidationErrors).length > 0) {
        throw new Error(`MercadoLibre validation failed: ${Object.values(commentValidationErrors).join(', ')}`)
      }

      // Check description length (same as individual publishing)
      const comments = userFormData.comments || item.vehicle.comments || ''

      if (!validateDescription(comments)) {
        throw new Error(`Vehicle description is too short (minimum 55 characters). Current length: ${comments.length}`)
      }

      console.log(
        `Publishing vehicle ${item.vehicle.plate} to MercadoLibre with branch:`,
        item.branch?.name || 'No branch data'
      )

      // Create MeliInputVehicle object using the EXACT same pattern as traditional publishing (Meli.tsx)
      const characteristics = (item.vehicle.characteristics || []).map((c: string) => invertedCharacteristics[c])

      // Prepare branch with meliRegionId - this is CRITICAL for location validation
      let branchWithRegion = null

      if (item.branch) {
        try {
          const municipalities = await fetchMunicipalities()

          if (municipalities) {
            const comuna = item.branch.municipality_format ?? ''
            const region = municipalities.find((r) => r.name === comuna)

            if (region) {
              const regionDetail = await fetchRegionDetail(region.regionId)

              if (regionDetail) {
                branchWithRegion = { ...item.branch, meliRegionId: regionDetail.meliRegionId }
              } else {
                branchWithRegion = { ...item.branch, meliRegionId: '5485' } // Default to Santiago
              }
            } else {
              branchWithRegion = { ...item.branch, meliRegionId: '5485' } // Default to Santiago
            }
          } else {
            branchWithRegion = { ...item.branch, meliRegionId: '5485' } // Default to Santiago
          }
        } catch (error) {
          console.warn('Error fetching municipality data:', error)
          branchWithRegion = { ...item.branch, meliRegionId: '5485' } // Default to Santiago
        }
      }

      // Prepare form data with user edits prioritized
      const formData = {
        ...userFormData,
        comments: comments,
        street: userFormData.street || (item.branch ? buildStreetAddress(item.branch) : ''),
      }

      // Use shared core for consistent payload construction
      const publishingConfig = {
        vehicle: item.vehicle,
        formData,
        userData,
        branch: branchWithRegion || item.branch,
        publicImages: userFormData.pictures || item.vehicle.pictures?.public || [],
        characteristics,
        region: { meliRegionId: branchWithRegion?.meliRegionId || '5485' },
      }

      const vehicleToPublish = buildMeliPayload(publishingConfig)

      // Validate final payload
      if (!vehicleToPublish.title || vehicleToPublish.title.trim().length === 0) {
        throw new Error('Vehicle title cannot be empty')
      }

      if (!vehicleToPublish.price || vehicleToPublish.price <= 0) {
        throw new Error('Vehicle price must be greater than 0')
      }

      console.log(`Attempting to publish vehicle: ${vehicleToPublish.title} - $${vehicleToPublish.price}`)

      const result = await meliPublish(vehicleToPublish, userData?.profile?._id ?? '')

      if (result?.ok) {
        if (userData?.profile) {
          await meliSetPublished(
            item.vehicle._id,
            userData.profile._id,
            result.inventoryIdentifier,
            result.permalink,
            result.title
          )
        }

        return result
      } else {
        throw new Error(result?.message || 'Publication failed - no error message provided')
      }
    },
    [
      userData,
      meliPublish,
      meliSetPublished,
      fetchMunicipalities,
      fetchRegionDetail,
      buildMeliPayload,
      buildStreetAddress,
    ]
  )

  const publishToChileAutos = useCallback(
    async (item: QueueItem) => {
      if (!userData?.organization) {
        throw new Error('User data or organization not available')
      }

      if (!userData.organization.external?.chileautos?.enabled) {
        throw new Error('ChileAutos publishing not enabled for this organization')
      }

      console.log(`Publishing vehicle ${item.vehicle.plate} to ChileAutos`)

      // CRITICAL FIX 1: Load draft data first (same as form components)
      const draftResult = await loadPublicationData(item.vehicle._id, 'chileautos')
      const draftData = draftResult.success && draftResult.data ? draftResult.data : {}

      // Combine user form data with draft data (form data takes priority)
      const userFormData = { ...draftData, ...(item.formData || {}) }

      // CRITICAL FIX 2: Apply better field resolution with robust defaults
      const resolvedValues = {
        type: userFormData.type || item.vehicle.type_format || item.vehicle.type || 'TYPES.VEHICLE.AUTOMOBILE',
        body: userFormData.body || item.vehicle.body_format || item.vehicle.body || 'Sedán',
        brand: userFormData.brand || item.vehicle.brand || 'Sin especificar',
        model: userFormData.model || item.vehicle.model || 'Sin especificar',
        fuel: userFormData.fuel || item.vehicle.fuel_format || item.vehicle.fuel || 'Bencina',
        gearbox: userFormData.gearbox || item.vehicle.gearbox_format || item.vehicle.gearbox || 'Manual',
        color: userFormData.color || item.vehicle.color || 'Blanco',
        title:
          userFormData.title ||
          `${item.vehicle.year} ${item.vehicle.brand} ${item.vehicle.model} ${item.vehicle.version}`,
        // CRITICAL: Use promo.message as fallback, NOT vehicle.comments (internal field)
        comments: userFormData.comments || item.vehicle.promo?.message || ' ',
      }

      const validationErrors = validateFields([
        { name: 'type', value: resolvedValues.type, rule: Rule.REQUIRED },
        { name: 'body', value: resolvedValues.body, rule: Rule.REQUIRED },
        { name: 'brand', value: resolvedValues.brand, rule: Rule.REQUIRED },
        { name: 'model', value: resolvedValues.model, rule: Rule.REQUIRED },
        { name: 'fuel', value: resolvedValues.fuel, rule: Rule.REQUIRED },
        { name: 'gearbox', value: resolvedValues.gearbox, rule: Rule.REQUIRED },
        { name: 'color', value: resolvedValues.color, rule: Rule.REQUIRED },
        { name: 'title', value: resolvedValues.title, rule: Rule.REQUIRED },
        { name: 'comments', value: resolvedValues.comments, rule: Rule.REQUIRED },
      ])

      if (Object.keys(validationErrors).length > 0) {
        throw new Error(`Validation failed: ${Object.values(validationErrors).join(', ')}`)
      }

      // Check for pictures
      const pictures = userFormData.pictures || item.vehicle.pictures?.public || []

      if (pictures.length === 0) {
        throw new Error('Vehicle must have at least one public image')
      }

      // Create ChileAutos payload using resolved values (following same pattern as Cha.tsx)
      // 🚨 IMPORTANT: ChileAutos does NOT support video, so we exclude videoUrl from the payload
      const { videoUrl, ...vehicleWithoutVideo } = item.vehicle

      const chaPayload: ChaInputVehicle = {
        ...vehicleWithoutVideo,
        ...resolvedValues, // Use resolved values with proper defaults
        organization: userData.organization,
        pictures: pictures,
        inventoryIdentifier: item.vehicle.external_chileautos?.identifier || '',
        characteristics: item.vehicle.characteristics || [],
      }

      const result = await chaPublish(chaPayload)

      if (result?.ok) {
        const id = result.inventoryIdentifier ?? ''
        const respSetPublished = await chaSetPublished(item.vehicle._id, userData.profile?._id ?? '', id)

        if (respSetPublished === 'OK') {
          return result
        } else {
          throw new Error('Failed to set published status')
        }
      } else {
        throw new Error(result?.message || 'ChileAutos publication failed')
      }
    },
    [userData, chaPublish, chaSetPublished]
  )

  const publishToYapo = useCallback(
    async (item: QueueItem) => {
      if (!userData?.organization) {
        throw new Error('User data or organization not available')
      }

      if (!userData.organization.external?.yapo?.enabled) {
        throw new Error('Yapo publishing not enabled for this organization')
      }

      console.log(`Publishing vehicle ${item.vehicle.plate} to Yapo`)

      // CRITICAL FIX 1: Load draft data first (same as form components)
      const draftResult = await loadPublicationData(item.vehicle._id, 'yapo')
      const draftData = draftResult.success && draftResult.data ? draftResult.data : {}

      // Combine user form data with draft data (form data takes priority)
      const userFormData = { ...draftData, ...(item.formData || {}) }

      // CRITICAL FIX 2: Apply better field resolution with robust defaults (KEEP ORIGINAL ENUM TYPE)
      const rawType = userFormData.type || item.vehicle.type_format || item.vehicle.type || 'TYPES.VEHICLE.AUTOMOBILE'
      const resolvedValues = {
        type: rawType, // Keep original enum type (same as individual publishing)
        body: userFormData.body || item.vehicle.body_format || item.vehicle.body || 'Sedán',
        brand: userFormData.brand || item.vehicle.brand || 'Sin especificar',
        model: userFormData.model || item.vehicle.model || 'Sin especificar',
        fuel: userFormData.fuel || item.vehicle.fuel_format || item.vehicle.fuel || 'Bencina',
        gearbox: userFormData.gearbox || item.vehicle.gearbox_format || item.vehicle.gearbox || 'Manual',
        color: userFormData.color || item.vehicle.color || 'Blanco',
        title:
          userFormData.title ||
          `${item.vehicle.year} ${item.vehicle.brand} ${item.vehicle.model} ${item.vehicle.version}`,
        comments:
          userFormData.comments ||
          item.vehicle.comments ||
          `${item.vehicle.brand} ${item.vehicle.model} ${item.vehicle.year} en excelente estado`,
        localization: userFormData.localization || '',
      }

      const validationErrors = validateFields([
        { name: 'type', value: resolvedValues.type, rule: Rule.REQUIRED },
        { name: 'body', value: resolvedValues.body, rule: Rule.REQUIRED },
        { name: 'brand', value: resolvedValues.brand, rule: Rule.REQUIRED },
        { name: 'model', value: resolvedValues.model, rule: Rule.REQUIRED },
        { name: 'year', value: userFormData.year || item.vehicle.year, rule: Rule.REQUIRED },
        { name: 'fuel', value: resolvedValues.fuel, rule: Rule.REQUIRED },
        { name: 'gearbox', value: resolvedValues.gearbox, rule: Rule.REQUIRED },
        { name: 'comments', value: resolvedValues.comments, rule: Rule.REQUIRED },
      ])

      if (Object.keys(validationErrors).length > 0) {
        throw new Error(`Validation failed: ${Object.values(validationErrors).join(', ')}`)
      }

      // Check for pictures
      const pictures = userFormData.pictures || item.vehicle.pictures?.public || []

      if (pictures.length === 0) {
        throw new Error('Vehicle must have at least one public image')
      }

      // Get region for yapoRegionId (same as Yapo.tsx)
      if (!item.branch) {
        throw new Error('Branch data is required for Yapo publishing')
      }

      // Get region for yapoRegionId (using same logic as individual Yapo.tsx)
      const municipalityName = item.branch.municipality_format

      if (!municipalityName) {
        throw new Error(
          `Branch municipality_format is missing. Branch: ${item.branch.name}. Please ensure the branch has a valid municipality assigned.`
        )
      }

      // Fetch all municipalities and find matching region (same as Yapo.tsx)
      const municipalities = await fetchMunicipalities()

      if (!municipalities || municipalities.length === 0) {
        throw new Error('Failed to fetch municipalities list')
      }

      // Find region by municipality name (same as Yapo.tsx)
      const matchedRegion = municipalities.find((r) => r.name === municipalityName)

      if (!matchedRegion) {
        // Try case-insensitive match as fallback
        const caseInsensitiveMatch = municipalities.find((r) => r.name.toLowerCase() === municipalityName.toLowerCase())

        if (!caseInsensitiveMatch) {
          throw new Error(`Municipality "${municipalityName}" not found in regions list. Branch: ${item.branch.name}`)
        }
      }

      const foundRegion =
        matchedRegion || municipalities.find((r) => r.name.toLowerCase() === municipalityName.toLowerCase())

      if (!foundRegion) {
        throw new Error(`Could not match municipality "${municipalityName}" to any region`)
      }

      // Fetch region details using the correct regionId (same as Yapo.tsx)
      const region = await fetchRegionDetail(foundRegion.regionId)

      if (!region) {
        throw new Error(
          `Failed to fetch region details for regionId: ${foundRegion.regionId}, municipality: ${municipalityName}`
        )
      }

      // Create Yapo payload using resolved values (following same pattern as Yapo.tsx)
      // 🚨 IMPORTANT: Yapo does NOT support video, so we exclude videoUrl from the payload
      const { videoUrl, ...vehicleWithoutVideo } = item.vehicle

      const yapoPayload: YapoInputVehicle = {
        ...vehicleWithoutVideo,
        ...resolvedValues, // Use resolved values with proper defaults and type conversion
        organization: userData.organization,
        pictures: pictures,
        branch: { ...item.branch, yapoRegionId: region.yapoRegionId },
        characteristics: item.vehicle.characteristics || [],
      }

      const result = await yapoPublish(yapoPayload)

      if (result?.ok) {
        const resp = await yapoSetPublished(item.vehicle._id, userData?.profile?._id ?? '')

        if (resp) {
          return result
        } else {
          throw new Error('Failed to set published status')
        }
      } else {
        throw new Error(result?.message || 'Yapo publication failed')
      }
    },
    [userData, yapoPublish, yapoSetPublished, fetchRegionDetail]
  )

  const addToQueue = useCallback(
    (
      vehicles: Array<{
        vehicle: any
        branch: any
        formData?: any
        platform?: 'mercadolibre' | 'chileautos' | 'yapo' | 'all'
      }>
    ) => {
      const queueItems: QueueItem[] = vehicles.map(({ vehicle, branch, formData, platform = 'mercadolibre' }) => ({
        vehicleId: vehicle._id,
        vehicle,
        branch,
        formData, // Include form data in queue items
        platform, // NEW: Platform to publish to
        operation: 'publish', // Default to publish
        platforms: [], // Not used for publish operations
        status: 'pending',
      }))

      setQueueState((prev) => ({
        ...prev,
        items: queueItems,
        totalItems: queueItems.length,
        completedCount: 0,
        failedCount: 0,
        currentIndex: -1,
      }))
    },
    []
  )

  // NEW: Add unpublish to queue function
  const addUnpublishToQueue = useCallback(
    (vehicles: Array<{ vehicle: any; platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'> }>) => {
      const queueItems: QueueItem[] = vehicles.map(({ vehicle, platforms }) => ({
        vehicleId: vehicle._id,
        vehicle,
        branch: null, // Not needed for unpublishing
        operation: 'unpublish',
        platform: 'all', // Placeholder - actual platforms are in platforms array
        platforms,
        status: 'pending',
      }))

      setQueueState((prev) => ({
        ...prev,
        items: queueItems,
        totalItems: queueItems.length,
        completedCount: 0,
        failedCount: 0,
        currentIndex: -1,
      }))
    },
    []
  )

  const processQueue = useCallback(async () => {
    if (!userData) {
      console.error('User data not available for queue processing')

      return
    }

    // Only fetch MercadoLibre category attributes if there are MercadoLibre items in the queue
    const hasMeliItems = queueState.items.some((item) => item.platform === 'mercadolibre' || item.platform === 'all')

    let categoryAttrs = null

    if (hasMeliItems) {
      try {
        console.log('Fetching MercadoLibre category attributes for bulk publishing...')
        categoryAttrs = await meliCategoryAttr()

        if (!categoryAttrs) {
          throw new Error('Failed to fetch MercadoLibre category attributes')
        }
        console.log('Successfully fetched MercadoLibre category attributes')
      } catch (error) {
        console.error('Failed to fetch MercadoLibre category data:', error)
        // Continue with fallback values but warn user
      }
    } else {
      console.log('Skipping MercadoLibre category attributes fetch - no MercadoLibre items in queue')
    }

    setQueueState((prev) => {
      if (prev.items.length === 0) return prev

      return { ...prev, isProcessing: true, currentIndex: 0 }
    })

    // We need to process items by accessing them through state updates
    let shouldContinue = true
    let currentIndex = 0

    while (shouldContinue) {
      // Get current item through a promise that resolves with state
      const getCurrentItem = (): Promise<QueueItem | null> => {
        return new Promise((resolve) => {
          setQueueState((prev) => {
            if (currentIndex >= prev.items.length) {
              shouldContinue = false
              resolve(null)

              return prev
            }
            const item = prev.items[currentIndex]
            resolve(item)

            return {
              ...prev,
              currentIndex,
              items: prev.items.map((qItem, index) =>
                index === currentIndex ? { ...qItem, status: 'publishing' } : qItem
              ),
            }
          })
        })
      }

      const item = await getCurrentItem()

      if (!item || !shouldContinue) break

      try {
        // Enhanced validation before attempting to publish
        if (!userData?.organization) {
          throw new Error('Organization data not available')
        }

        // Validate vehicle data
        if (!item.vehicle) {
          throw new Error('Vehicle data is missing')
        }

        // Check for required vehicle fields
        const requiredFields = ['brand', 'model', 'year', 'plate']
        const missingFields = requiredFields.filter((field) => !item.vehicle[field])

        if (missingFields.length > 0) {
          throw new Error(`Missing required vehicle fields: ${missingFields.join(', ')}`)
        }

        // Check for public images (only for publish operations)
        if (
          item.operation === 'publish' &&
          (!item.vehicle.pictures?.public || item.vehicle.pictures.public.length === 0)
        ) {
          throw new Error('Vehicle must have at least one public image')
        }

        // Handle both publish and unpublish operations
        let result

        if (item.operation === 'unpublish') {
          // Process unpublish for each platform
          const results = []

          for (const platform of item.platforms) {
            try {
              let platformResult

              switch (platform) {
                case 'mercadolibre':
                  platformResult = await unpublishFromMercadoLibre(item.vehicle)
                  break
                case 'chileautos':
                  platformResult = await unpublishFromChileAutos(item.vehicle)
                  break
                case 'yapo':
                  platformResult = await unpublishFromYapo(item.vehicle)
                  break
              }
              results.push({ platform, success: true, result: platformResult })
            } catch (error) {
              results.push({
                platform,
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
              })
            }
          }

          // Check if at least one platform succeeded
          const successfulPlatforms = results.filter((r) => r.success)

          if (successfulPlatforms.length === 0) {
            throw new Error(
              `Failed to unpublish from all platforms: ${results.map((r) => `${r.platform}: ${r.error}`).join(', ')}`
            )
          }

          result = {
            ok: true,
            platforms: results,
            message: `Unpublished from ${successfulPlatforms.length}/${results.length} platforms`,
          }
        } else {
          // Platform-specific publishing
          switch (item.platform) {
            case 'mercadolibre':
              result = await publishToMercadoLibre(item)
              break
            case 'chileautos':
              result = await publishToChileAutos(item)
              break
            case 'yapo':
              result = await publishToYapo(item)
              break
            case 'all':
              // Simple sequential approach - just call the individual platform functions
              console.log(`Publishing vehicle ${item.vehicle.plate} to all platforms`)

              const platforms = ['mercadolibre', 'chileautos', 'yapo'] as const
              const results = []

              for (const platform of platforms) {
                try {
                  let platformResult

                  // Simply call the individual platform functions that already work
                  switch (platform) {
                    case 'mercadolibre':
                      if (userData.organization.external?.meli?.enabled) {
                        // Create a temporary item for this platform
                        const tempMeliItem = { ...item, platform: 'mercadolibre' as const }
                        platformResult = await publishToMercadoLibre(tempMeliItem)
                        results.push({ platform, success: true, result: platformResult })
                      } else {
                        results.push({ platform, success: false, error: 'MercadoLibre not enabled' })
                      }
                      break

                    case 'chileautos':
                      if (userData.organization.external?.chileautos?.enabled) {
                        // Create a temporary item for this platform
                        const tempChaItem = { ...item, platform: 'chileautos' as const }
                        platformResult = await publishToChileAutos(tempChaItem)
                        results.push({ platform, success: true, result: platformResult })
                      } else {
                        results.push({ platform, success: false, error: 'ChileAutos not enabled' })
                      }
                      break

                    case 'yapo':
                      if (userData.organization.external?.yapo?.enabled) {
                        // Create a temporary item for this platform
                        const tempYapoItem = { ...item, platform: 'yapo' as const }
                        platformResult = await publishToYapo(tempYapoItem)
                        results.push({ platform, success: true, result: platformResult })
                      } else {
                        results.push({ platform, success: false, error: 'Yapo not enabled' })
                      }
                      break
                  }
                } catch (error) {
                  results.push({
                    platform,
                    success: false,
                    error: error instanceof Error ? error.message : 'Unknown error',
                  })
                }
              }

              // Check if at least one platform succeeded
              const successfulPlatforms = results.filter((r) => r.success)

              if (successfulPlatforms.length === 0) {
                throw new Error(
                  `Failed to publish to all platforms: ${results.map((r) => `${r.platform}: ${r.error}`).join(', ')}`
                )
              }

              result = {
                ok: true,
                platforms: results,
                message: `Published to ${successfulPlatforms.length}/${results.length} platforms`,
              }
              break
            default:
              throw new Error(`Unsupported platform: ${item.platform}`)
          }
        }

        // Check if the publication was successful
        if (result?.ok) {
          console.log(`Successfully published vehicle ${item.vehicle.plate} to ${item.platform}`)

          // Update item status to completed
          setQueueState((prev) => ({
            ...prev,
            completedCount: prev.completedCount + 1,
            items: prev.items.map((qItem, index) =>
              index === currentIndex ? { ...qItem, status: 'completed', result } : qItem
            ),
          }))
        } else {
          const errorMessage = result?.message || 'Publication failed - no error message provided'
          console.error(`Failed to publish vehicle ${item.vehicle.plate}:`, errorMessage)
          throw new Error(errorMessage)
        }
      } catch (error) {
        const errorMessage = (error as Error)?.message || 'Unknown error occurred during publication'
        console.error(`Error publishing vehicle ${item.vehicle?.plate || 'unknown'}:`, errorMessage)

        // Update item status to failed
        setQueueState((prev) => ({
          ...prev,
          failedCount: prev.failedCount + 1,
          items: prev.items.map((qItem, index) =>
            index === currentIndex
              ? {
                  ...qItem,
                  status: 'failed',
                  error: errorMessage,
                }
              : qItem
          ),
        }))
      }

      currentIndex++

      // Add a small delay between publications to avoid rate limiting
      if (shouldContinue && currentIndex < queueState.items.length) {
        await new Promise((resolve) => setTimeout(resolve, 2000)) // 2 second delay
      }
    }

    // Mark processing as complete - NO auto-close sequence
    setQueueState((prev) => ({
      ...prev,
      isProcessing: false,
      currentIndex: -1,
      completionState: 'completed',
      canAutoClose: false, // Don't auto-close, let user close manually
    }))

    // Removed: startAutoCloseCountdown() - no auto-close functionality
  }, [
    userData,
    publishToMercadoLibre,
    publishToChileAutos,
    publishToYapo,
    unpublishFromMercadoLibre,
    unpublishFromChileAutos,
    unpublishFromYapo,
    meliCategoryAttr,
  ])

  // NEW: Cancel auto-close functionality
  const cancelAutoClose = useCallback(() => {
    setQueueState((prev) => ({
      ...prev,
      canAutoClose: false,
      completionState: 'completed', // Stay in completed state without auto-closing
    }))
  }, [])

  // NEW: Trigger page refresh (for parent component to use)
  const triggerPageRefresh = useCallback(() => {
    console.log('Triggering full page refresh after successful publication')
    window.location.reload()
  }, [])

  const clearQueue = useCallback(() => {
    setQueueState({
      items: [],
      isProcessing: false,
      currentIndex: -1,
      totalItems: 0,
      completedCount: 0,
      failedCount: 0,
      // NEW: Reset auto-completion state
      completionState: null,
      autoCloseCountdown: 0,
      canAutoClose: false,
    })
  }, [])

  return {
    queueState,
    addToQueue,
    addUnpublishToQueue, // NEW: Export unpublish function
    processQueue,
    clearQueue,
    // NEW: Export auto-close functions
    cancelAutoClose,
    triggerPageRefresh,
  }
}

export default usePublishQueue
